import React, { FC, VFC } from 'react'
import styled from 'styled-components'

import useSiteMetadata from '../../../../../../hooks/useSiteMetadata'
import { usePractitionerSettings } from '../../../../../../lib/practitioner-settings/hooks/usePractitionerSettings'
import {
  commissionsCashBack,
  commissionsCashBackPercent,
  commissionsPayoutMinimum,
} from '../../../../../../lib/rewards/commissions'
import { dropZeroCents, formatAsCurrencyWithCommasOrZero } from '../../../../../../utils/priceTools'
import { RewardsInformationTooltip } from '../../RewardsInformationTooltip'
import { InformationIcon, NoticeCard, NoticeLink, NoticeText } from '../../RewardsNotice'
import {
  DetailsPayoutsCard,
  DetailsPayoutsCardActions,
  DetailsPayoutsCardAmount,
  DetailsPayoutsCardAmountLabel,
  DetailsPayoutsCardAmountSubtext,
  DetailsPayoutsCardContent,
  DetailsPayoutsCardHeading,
} from '../DetailsPayoutsCard'
import { CommissionsRequestPayoutButton } from './CommissionsRequestPayoutButton'

export interface CommissionsCardProps {
  availableBalance: {
    commissionsBalance: number
  }
  refetchBalance: () => Promise<void>
}

const AmountAvailableLabelCashback = styled.strong`
  color: ${({ theme }) => theme.colors.blackPearlBlue.shade400};
`

const CashbackLabel = styled.div`
  font-size: 16px;
  margin-bottom: 0;
  font-family: ${({ theme }) => theme.fonts.global.headers};
  color: ${({ theme }) => theme.colors.blackPearlBlue.shade400};
`

const Cashback = styled.span`
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 0;
  margin-right: 5px;
  margin-left: 5px;
  font-family: ${({ theme }) => theme.fonts.global.headers};
  color: ${({ theme }) => theme.colors.blackPearlBlue.shade400};
  line-height: 1;
`

const NoticeCardContainer = styled.div`
  margin: 30px 0;
`

const CommissionsOptOutNotice: VFC = () => (
  <NoticeCardContainer>
    <NoticeCard>
      <InformationIcon />
      <NoticeText>
        Currently not earning commissions.{' '}
        <NoticeLink to="/account/settings/rewards-program">
          Enroll to commissions in Rewards Program
        </NoticeLink>
      </NoticeText>
    </NoticeCard>
  </NoticeCardContainer>
)

export const CommissionsCard: FC<CommissionsCardProps> = ({ availableBalance, refetchBalance }) => {
  const { commissionsBalance } = availableBalance
  const { featureFlags } = useSiteMetadata() ?? {}

  const [settings] = usePractitionerSettings()
  const optedOutOfCommissions = settings?.practitioner?.commissionsOptOutFormReceived ?? false
  const isEligibleForCommissions = !settings?.practitioner?.ineligibleForCommissions

  if (!isEligibleForCommissions) {
    return null
  }

  return (
    <DetailsPayoutsCard>
      <DetailsPayoutsCardContent>
        <DetailsPayoutsCardHeading>Commissions</DetailsPayoutsCardHeading>
        {optedOutOfCommissions ? (
          <CommissionsOptOutNotice />
        ) : (
          <>
            <DetailsPayoutsCardAmountLabel>
              <DetailsPayoutsCardAmount>
                {dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: commissionsBalance }))}
              </DetailsPayoutsCardAmount>
              available
              {featureFlags?.commissionsCashBack && (
                <AmountAvailableLabelCashback>
                  {' '}
                  +{commissionsCashBackPercent}% cash-back
                  <RewardsInformationTooltip>
                    {commissionsCashBackPercent}% cash-back is applicable to Commissions upon
                    invoice. Store Credits and Rebates do not qualify for cash-back awards.
                  </RewardsInformationTooltip>
                </AmountAvailableLabelCashback>
              )}
            </DetailsPayoutsCardAmountLabel>
            {featureFlags?.commissionsCashBack && (
              <CashbackLabel>
                Earn
                <Cashback>
                  {dropZeroCents(
                    formatAsCurrencyWithCommasOrZero({
                      amount: commissionsCashBack(commissionsBalance),
                    }),
                  )}
                </Cashback>
                cash-back for Commissions spent online
              </CashbackLabel>
            )}
            <DetailsPayoutsCardAmountSubtext>
              {dropZeroCents(
                formatAsCurrencyWithCommasOrZero({ amount: commissionsPayoutMinimum }),
              )}{' '}
              Minimum to cash out.
            </DetailsPayoutsCardAmountSubtext>
          </>
        )}
      </DetailsPayoutsCardContent>
      <DetailsPayoutsCardActions>
        <CommissionsRequestPayoutButton
          availableBalance={availableBalance}
          refetchBalance={refetchBalance}
        />
      </DetailsPayoutsCardActions>
    </DetailsPayoutsCard>
  )
}
